import type {
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopedContentRow,
} from '../types/domain';
import { parseMemoFieldStorageDeep } from './programCellDisplayText';
import {
  parseProgramLearningContentFieldsFromEditor,
  readProgramLearningContentFields,
  type ProgramLearningContentRowLike,
} from './programLearningContentFields';
import type { ProgramProgressRow } from './programProgressRows';
import { isPersistableUuid } from './programContentSlideOwner';

export type ProgramContentWorksheetOwnerRef = {
  kind: 'worksheet_ref';
  id: string;
};

export function resolveWorksheetRefIdFromLearningText(learningText: string | null | undefined): string {
  const refId = parseProgramLearningContentFieldsFromEditor(learningText).worksheetRefId?.trim() ?? '';
  return refId && isPersistableUuid(refId) ? refId : '';
}

export function resolveWorksheetRefIdFromLearningContent(
  source: string | ProgramLearningContentRowLike | null | undefined
): string {
  if (source == null) return '';
  const fields =
    typeof source === 'string'
      ? parseProgramLearningContentFieldsFromEditor(source)
      : readProgramLearningContentFields(source);
  const refId = fields.worksheetRefId?.trim() ?? '';
  return refId && isPersistableUuid(refId) ? refId : '';
}

/** 활동지 인디케이터: draft·program cell 중 worksheetRefId가 있는 쪽 우선 */
export function resolveProgramWorksheetRefIdForIndicator(
  ...sources: Array<string | ProgramLearningContentRowLike | null | undefined>
): string {
  for (const src of sources) {
    const ref = resolveWorksheetRefIdFromLearningContent(src);
    if (ref) return ref;
  }
  return '';
}

/**
 * 프로그램 표 활동지 연결 owner.
 * 신규: 학습내용 JSON의 `worksheetRefId` (프레젠테이션 `presentationRefId`와 동일 우선순위).
 */
export function resolveProgramContentWorksheetOwner(
  _mode: 'default' | 'fixed_to_unit' | 'project_to_template' | undefined,
  _row: ProgramProgressRow,
  _cell: ProgramProgressCell | undefined,
  learningText?: string | null
): ProgramContentWorksheetOwnerRef | null {
  const worksheetRefId = resolveWorksheetRefIdFromLearningText(learningText ?? '');
  if (worksheetRefId) return { kind: 'worksheet_ref', id: worksheetRefId };
  return null;
}

export function resolveAnnualWorksheetRefId(params: {
  isFixedSlot: boolean;
  fixedRow: ProgramFixedContentRow | null;
  scopedRow: ProgramScopedContentRow | null;
  programCell: ProgramProgressCell | undefined;
}): string {
  if (params.isFixedSlot) {
    return resolveWorksheetRefIdFromLearningContent(params.fixedRow);
  }
  return (
    resolveWorksheetRefIdFromLearningContent(params.scopedRow) ||
    resolveWorksheetRefIdFromLearningContent(params.programCell)
  );
}

export function programContentWorksheetPresenceKey(owner: ProgramContentWorksheetOwnerRef): string {
  return `worksheet_ref:${owner.id.trim()}`;
}
